import axios from 'axios'
// 修正 1: 确保引入的是路由实例。通常 Vue 2 默认导出就是实例
import router from "@/router";
import { Message } from 'element-ui' // 网络和业务错误使用可关闭的提示条，不再阻塞页面与考试计时。
import { createRequestErrorNotice } from './requestErrorNotice' // 多接口共用故障去重，避免轮询重复提示。

const service = axios.create({
    baseURL: process.env.VUE_APP_API_URL || 'https://apps.hzaqjk.com',
    // baseURL: 'https://apps.hzaqjk.com',
    timeout: 10000
})

// 修正 2: 锁必须定义在拦截器外部，才能在多个请求之间共享
let isRelogging = false;
let noticeLoginToken = ''; // 仅在内存中比较普通登录会话是否已经更换，不新增凭证存储。
const errorNotice = createRequestErrorNotice(message => Message.error({ message, showClose: true, duration: 5000 })) // 自动消失或手动关闭均不会重置故障去重。

service.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token')
        if (token && token !== noticeLoginToken) { noticeLoginToken = token; errorNotice.reset() } // 新用户登录后可以正常收到自己的错误提示。
        errorNotice.begin(config) // 在发出请求时标记序号，防止旧的成功响应错误地解除新故障。
        if (token) {
            config.headers['Authorization'] = token
        }
        return config
    },
    error => Promise.reject(error)
)

service.interceptors.response.use(
    response => {
        const res = response.data || {} // 服务异常返回空内容时也走统一失败流程，不抛空对象访问异常。

        if (res.code === 401) {
            // 1. 不管在哪里，只要 401 就先清理缓存，确保状态同步
            // localStorage.removeItem('token');
            // localStorage.removeItem('userInfo');
            // localStorage.removeItem('userVip');
            localStorage.clear()

            // 2. 获取当前路径
            const currentPath = router?.currentRoute?.path || '';

            // 3. 如果已经在首页/登录页了，直接停止，不需要再执行跳转和弹窗
            if (currentPath === '/') {
                return Promise.reject(new Error('登录已失效'));
            }

            // 4. 只有不在首页时，才执行“加锁跳转”逻辑
            if (!isRelogging) {
                isRelogging = true;

                router.push('/').then(() => {
                    errorNotice.failure({ ...response.config, silentError: false }, res.message || '登录状态已过期，请重新登录', 'login-expired'); // 保留原登录失效跳转，用一次提示代替阻塞弹窗。
                }).catch(err => {
                    if (err.name !== 'NavigationDuplicated') console.error(err);
                }).finally(() => {
                    setTimeout(() => {
                        isRelogging = false;
                    }, 1000);
                });
            }

            // 5. 为了消灭你之前提到的第二次失效时的红字报错
            // 对于被“锁”住的后续并发请求，返回一个 pending 的 Promise
            if (isRelogging && currentPath !== '/') {
                return new Promise(() => {});
            }

            return Promise.reject(new Error('Authentication failed'));
        }

        if (Number(res.code) === 4611) {
            window.dispatchEvent(new CustomEvent('exam-experience-eligibility-denied')) // 撤销资格仅影响考试体验，保留普通登录并静默更新菜单。
            return Promise.reject(Object.assign(new Error(res.message || '该手机号未开通电脑终端使用权限'), { code: res.code })) // 轮询遇到撤权不重复弹窗。
        }
        if (Number(res.code) === 4610) {
            window.dispatchEvent(new CustomEvent('exam-experience-access-denied')) // 权限刚被主控关闭时刷新服务器范围，避免并发页面请求反复弹错。
            return Promise.reject(Object.assign(new Error(res.message || '主控已关闭自由练习'), { code: res.code })) // 仍拒绝本次数据请求，不转为401或伪装成功。
        }
        if (res.code !== 200) {
            const systemFailure = !res.message || res.message === '系统错误' || (Number(res.code) >= 500 && Number(res.code) < 600) // 无有效业务响应、系统错误和5xx统一视为服务故障。
            const message = systemFailure ? '服务暂时不可用，请稍后重试' : res.message // 不再给持续故障反复显示没有帮助的“系统错误”。
            errorNotice.failure(response.config, message, systemFailure ? 'service-unavailable' : 'business:' + res.code + ':' + message) // 同一服务故障跨接口合并，明确的业务错误按内容区分。
            return Promise.reject(Object.assign(new Error(message), { code: res.code })) // 保留业务错误码和可读错误，操作令牌失效不等于普通登录401。
        }
        errorNotice.success(response.config) // 只有该接口在失败之后重新成功，才允许提示它的下一轮故障。
        return res
    },
    error => {
        // 修正 4: 处理 HTTP 状态码级别的 401（后端如果返回 401 状态码也会走到这里）
        if (error.response && error.response.status === 401) {
            if (!isRelogging) {
                isRelogging = true;
                localStorage.clear();
                router.push('/').finally(() => { isRelogging = false; });
                errorNotice.failure({ ...error.config, silentError: false }, '登录已过期', 'login-expired'); // 多次HTTP 401共用一次非阻塞提示。
            }
        } else if (!axios.isCancel(error) && error.code !== 'ERR_CANCELED') {
            const message = !error.response ? (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' ? '请求超时，请检查网络后重试' : '网络连接异常，请检查网络后重试') : '服务暂时不可用，请稍后重试' // 取消请求不报错；断网、超时和服务故障给出可理解的提示。
            errorNotice.failure(error.config, message, 'service-unavailable') // 同一轮网络/服务故障只提示一次，后台 silentError 请求继续静默。
        }
        return Promise.reject(error)
    }
)

export default service
